import classNames from 'class-names';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import useSmallScreen from '../../lib/hooks/useSmallScreen';

import styles from './slider.module.css';

const boundRange = (value, minValue = value, maxValue = value) => {
  return Math.max(Math.min(value, maxValue), minValue);
};

const getValueBox = (
  currentValue,
  showUnits,
  unitLabel,
  valueBoxSize,
  disabled = false,
) => {
  // TODO: Change from function to a component pulled from a separate file.
  return (
    <div className="d-flex align-items-center">
      <div
        className={classNames(
          'd-flex justify-content-center align-items-center p-2',
          !disabled && 'text-white',
        )}
        style={{
          width: valueBoxSize.width,
          height: valueBoxSize.height,
          backgroundColor: disabled ? '#adb5bd' : '#0086cd',
        }}
      >
        {currentValue}
      </div>
      {showUnits && unitLabel !== '' && (
        <div className="d-flex justify-content-center pl-2">{unitLabel}</div>
      )}
    </div>
  );
};

const Slider = ({
  disabled = false,
  minValue = 0,
  maxValue = 100,
  defaultValue = 0,
  step = 1,
  showLabels = true,
  minLabel = 'min',
  maxLabel = 'max',
  showValue = true,
  valueBoxSize = {
    width: 50,
    height: 40,
  },
  showUnits = false,
  unitLabel,
  onSlideChange,
}) => {
  const isExtraSmallScreen = useSmallScreen('xs');
  const [currentValue, setCurrentValue] = useState(defaultValue);

  const sliderStyle = {
    '--percent-filled': `${(currentValue * 100) / (maxValue - minValue)}%`,
    '--slider-color': disabled ? '#adb5bd' : '#0086cd',
    '--slider-opacity': disabled ? 1 : 0.7,
    '--slider-cursor': disabled ? 'default' : 'pointer',
  };

  // TODO: - Ensure that slider can always reach min and max values with its step size.
  //       - Decouple screen size from slider size and value box placement.

  return (
    <div>
      <div className="d-flex justify-content-center align-items-center">
        <div className="d-flex flex-grow-1 flex-column text-gray">
          {showLabels && showValue && !isExtraSmallScreen && (
            <div className="d-flex">{'\u00A0'}</div>
          )}
          <input
            type="range"
            disabled={disabled}
            value={currentValue}
            min={minValue}
            max={maxValue}
            step={step}
            className={classNames(
              isExtraSmallScreen ? styles['slider-lg'] : styles.slider,
            )}
            style={sliderStyle}
            onChange={(event) => {
              const boundedValue = boundRange(
                event.target.value,
                minValue,
                maxValue,
              );
              setCurrentValue(boundedValue);
              onSlideChange(boundedValue);
            }}
          />
          {showLabels && (
            <div className="d-flex justify-content-between">
              <div>{minLabel}</div>
              <div>{maxLabel}</div>
            </div>
          )}
        </div>
        {!isExtraSmallScreen && showValue && (
          <div className="pl-5">
            {getValueBox(
              currentValue,
              showUnits,
              unitLabel,
              valueBoxSize,
              disabled,
            )}
          </div>
        )}
      </div>
      {isExtraSmallScreen && showValue && (
        <div className="d-flex justify-content-center">
          {getValueBox(
            currentValue,
            showUnits,
            unitLabel,
            valueBoxSize,
            disabled,
          )}
        </div>
      )}
    </div>
  );
};

Slider.propTypes = {
  disabled: PropTypes.bool,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  defaultValue: PropTypes.number,
  step: PropTypes.number,
  showLabels: PropTypes.bool,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  showValue: PropTypes.bool,
  valueBoxSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  showUnits: PropTypes.bool,
  unitLabel: PropTypes.string,
  onSlideChange: PropTypes.func.isRequired,
};

export default Slider;
